import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { interval, Subscription, firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-connection-monitor',
    imports: [
        CommonModule
    ],
    template: `
    <div class="connection-monitor-wrapper">
      <div class="connection-monitor">

          <i class="{{ iconName }}"
            [ngStyle]="{
              'color': iconColor,
              'font-size': '1.8rem'
              }">
          </i>

        <div class="stats">
          <p title="Response Time: {{ lastResponseTime }}ms" style="font-size: 1.3em">{{ connectionStatus }}</p>
        </div>
      </div>

      <div class="auth-advisory" *ngIf="!authOk">
        <i class="bi bi-exclamation-triangle-fill"></i>
        <span>{{ authAdvisory }}</span>
      </div>
    </div>
  `,
    styles: [`
    .connection-monitor {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 16px;
      border: 1px solid #ddd;
      border-radius: 8px;
    }
    .status-icon {
      transition: color 0.3s ease;
    }
    .stats p {
      margin: 4px 0;
      font-size: 14px;
    }
    .connection-monitor-wrapper {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .auth-advisory {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border: 1px solid #fde68a;
      background: #fffbeb;
      border-radius: 8px;
      color: #92400e;
      font-size: 13px;
    }
  `]
})
export class ConnectionMonitorComponent implements OnInit, OnDestroy {
  private subscription?: Subscription;
  private readonly CHECK_INTERVAL = 15000; // Check every 15 seconds
  private apiUrl = environment.apiConfig.uri;

  // Assumed average thresholds (in ms)
  private readonly GOOD_THRESHOLD = 500;
  private readonly MODERATE_THRESHOLD = 1000;

  lastResponseTime: number = 0;
  iconName: string = 'bi bi-reception-4';
  iconColor: string = '#22c55e'; // green
  connectionStatus: string = 'Good';

  // Auth health is tracked separately from network latency: a healthy ping
  // doesn't guarantee a submit will succeed if the user's token has gone stale.
  authOk: boolean = true;
  authAdvisory: string = '';

  constructor(
    private http: HttpClient,
    private msalService: MsalService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Start monitoring on component initialization
    this.startMonitoring();
  }

  ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    this.subscription?.unsubscribe();
  }

  private startMonitoring(): void {
    this.subscription = interval(this.CHECK_INTERVAL).subscribe(() => {
      this.checkApiLatency();
      this.checkAuthHealth();
    });

    // Make initial check immediately
    this.checkApiLatency();
    this.checkAuthHealth();
  }

  // Mirrors the token-acquisition the AuthInterceptor performs on submit. A
  // silent-token failure is exactly what stamps the X-Auth-Fail header, so
  // testing it here surfaces the same risk before the user submits. Advisory
  // only — the public JSA route still accepts an untokenized submit.
  private async checkAuthHealth(): Promise<void> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        this.setAuthAdvisory('You appear to be signed out — refreshing the page before submitting is recommended.');
        return;
      }
      await firstValueFrom(this.msalService.acquireTokenSilent({
        scopes: [...environment.apiConfig.scopes],
        account,
      }));
      this.setAuthOk();
    } catch {
      this.setAuthAdvisory('Your sign-in may have expired — refreshing the page before submitting is recommended.');
    }
  }

  private setAuthOk(): void {
    this.authOk = true;
    this.authAdvisory = '';
    // acquireTokenSilent resolves outside Angular's zone; force a CD pass.
    this.cdr.detectChanges();
  }

  private setAuthAdvisory(message: string): void {
    this.authOk = false;
    this.authAdvisory = message;
    this.cdr.detectChanges();
  }

  private async checkApiLatency(): Promise<void> {
    const startTime = performance.now();

    try {
      // await fetch from API
      await fetch(this.apiUrl + '/ping');

      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);

      this.updateConnectionQuality(responseTime);
    } catch (error) {
      console.error('API request failed:', error);
      // Treat failed requests as very slow (e.g., 5000ms)
      this.updateConnectionQuality(5000);
    }
  }

  private updateConnectionQuality(responseTime: number): void {
    this.lastResponseTime = responseTime;

    // Update icon color based on current response time
    // assuming it represents average connection quality
    if (responseTime < this.GOOD_THRESHOLD) {
      // Good connection - Green
      this.iconName = 'bi bi-reception-4'
      this.iconColor = '#22c55e';
      this.connectionStatus = 'Good Connection';
    } else if (responseTime < this.MODERATE_THRESHOLD) {
      // Moderate connection - Yellow
      this.iconName = 'bi bi-reception-2'
      this.iconColor = '#eab308';
      this.connectionStatus = 'Moderate Connection';
    } else {
      // Poor connection - Red
      this.iconName = 'bi bi-reception-1'
      this.iconColor = '#ef4444';
      this.connectionStatus = 'Poor Connection';
    }
  }
}